import { supabase } from '../lib/supabase';

// Helper to convert snake_case to camelCase
function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
}

// Helper to convert camelCase to snake_case
function toSnakeCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
}

export const violationChargeService = {
  // Create a new violation charge
  async createCharge(chargeData) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Upload documentation files if provided
      let documentationUrls = [];
      if (chargeData?.documentationFiles && chargeData?.documentationFiles?.length > 0) {
        documentationUrls = await this.uploadDocumentation(chargeData?.documentationFiles);
      }

      // Prepare charge data
      const chargePayload = toSnakeCase({
        bookingId: chargeData?.bookingId,
        ownerId: user?.id,
        renterId: chargeData?.renterId,
        chargeAmount: chargeData?.chargeAmount,
        violationReason: chargeData?.violationReason,
        violationDescription: chargeData?.violationDescription,
        documentationUrls: documentationUrls,
        chargeStatus: 'pending',
      });

      const { data, error } = await supabase
        ?.from('rental_violation_charges')
        ?.insert(chargePayload)
        ?.select()
        ?.single();

      if (error) {
        console.error('Create charge error:', error);
        throw new Error(error?.message || 'Failed to create violation charge');
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Create violation charge error:', error);
      throw error;
    }
  },

  // Upload documentation files to Supabase Storage
  async uploadDocumentation(files) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const uploadPromises = files?.map(async (file) => {
        const fileExt = file?.name?.split('.')?.pop();
        const fileName = `${user?.id}/${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}.${fileExt}`;

        const { data, error } = await supabase?.storage
          ?.from('violation-documentation')
          ?.upload(fileName, file);

        if (error) {
          console.error('File upload error:', error);
          throw new Error(`Failed to upload ${file?.name}`);
        }

        // Get signed URL (valid for 1 year)
        const { data: urlData, error: urlError } = await supabase?.storage
          ?.from('violation-documentation')
          ?.createSignedUrl(fileName, 31536000); // 1 year in seconds

        if (urlError) {
          console.error('Signed URL error:', urlError);
          throw new Error('Failed to generate file URL');
        }

        return urlData?.signedUrl;
      });

      return await Promise.all(uploadPromises);
    } catch (error) {
      console.error('Upload documentation error:', error);
      throw error;
    }
  },

  // Process charge through Stripe
  async processChargePayment(chargeId) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase?.functions?.invoke('create-violation-charge', {
        body: { chargeId, action: 'create_payment' }
      });

      if (error) {
        throw new Error(error?.message || 'Failed to process charge payment');
      }

      return data;
    } catch (error) {
      console.error('Process charge payment error:', error);
      throw error;
    }
  },

  // Get charges for a booking
  async getChargesByBooking(bookingId) {
    try {
      const { data, error } = await supabase
        ?.from('rental_violation_charges')
        ?.select(`
          *,
          owner:user_profiles!rental_violation_charges_owner_id_fkey(id, full_name, email),
          renter:user_profiles!rental_violation_charges_renter_id_fkey(id, full_name, email)
        `)
        ?.eq('booking_id', bookingId)
        ?.order('created_at', { ascending: false });

      if (error) {
        console.error('Get charges error:', error);
        if (error?.code === 'PGRST116') {
          return [];
        }
        throw new Error(error?.message || 'Failed to fetch charges');
      }

      return data ? data?.map(toCamelCase) : [];
    } catch (error) {
      console.error('Get charges by booking error:', error);
      throw error;
    }
  },

  // Get all charges for owner
  async getOwnerCharges() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        ?.from('rental_violation_charges')
        ?.select(`
          *,
          booking:bookings!rental_violation_charges_booking_id_fkey(*),
          renter:user_profiles!rental_violation_charges_renter_id_fkey(id, full_name, email)
        `)
        ?.eq('owner_id', user?.id)
        ?.order('created_at', { ascending: false });

      if (error) {
        console.error('Get owner charges error:', error);
        if (error?.code === 'PGRST116') {
          return [];
        }
        throw new Error(error?.message || 'Failed to fetch charges');
      }

      return data ? data?.map(toCamelCase) : [];
    } catch (error) {
      console.error('Get owner charges error:', error);
      throw error;
    }
  },

  // Get all charges for renter
  async getRenterCharges() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        ?.from('rental_violation_charges')
        ?.select(`
          *,
          booking:bookings!rental_violation_charges_booking_id_fkey(*),
          owner:user_profiles!rental_violation_charges_owner_id_fkey(id, full_name, email)
        `)
        ?.eq('renter_id', user?.id)
        ?.order('created_at', { ascending: false });

      if (error) {
        console.error('Get renter charges error:', error);
        if (error?.code === 'PGRST116') {
          return [];
        }
        throw new Error(error?.message || 'Failed to fetch charges');
      }

      return data ? data?.map(toCamelCase) : [];
    } catch (error) {
      console.error('Get renter charges error:', error);
      throw error;
    }
  },

  // Update charge status
  async updateChargeStatus(chargeId, status, disputeReason = null) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const updateData = {
        charge_status: status,
        updated_at: new Date()?.toISOString(),
      };

      if (status === 'disputed' && disputeReason) {
        updateData.dispute_reason = disputeReason;
        updateData.dispute_date = new Date()?.toISOString();
      }

      if (status === 'paid') {
        updateData.paid_at = new Date()?.toISOString();
      }

      const { data, error } = await supabase
        ?.from('rental_violation_charges')
        ?.update(updateData)
        ?.eq('id', chargeId)
        ?.select()
        ?.single();

      if (error) {
        console.error('Update charge status error:', error);
        throw new Error(error?.message || 'Failed to update charge status');
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Update charge status error:', error);
      throw error;
    }
  },
};